import { Stack, Tooltip, Typography } from "@mui/material"
import React from "react"
import { useTranslation } from "react-i18next"
import LanguageSelect from "src/components/LanguageSelect"
import { Language } from "src/utils/useLanguageList"
import { useResponsive } from "src/utils/useResponsive"

interface LanguagesSelectionProps {
  langFrom: Language
  setLangFrom: (langFrom: Language) => void
  langsTo: Language[]
  setLangsTo: (langsTo: Language[]) => void
  useMultiTargetLangs: boolean
  stackSpacing: number
  allowedLanguages: Language[]
  allowSameSourceAndTarget: boolean
  requireSameSourceAndTarget: boolean
}

export const LanguagesSelection = ({
  langFrom,
  setLangFrom,
  langsTo,
  setLangsTo,
  useMultiTargetLangs,
  stackSpacing,
  allowedLanguages,
  allowSameSourceAndTarget,
  requireSameSourceAndTarget
}: LanguagesSelectionProps) => {
  const { t } = useTranslation("common")
  const { isMobile } = useResponsive()

  const handleSourceLangChange = (newLangFrom: Language) => {
    setLangFrom(newLangFrom)
    if (requireSameSourceAndTarget) {
      setLangsTo([newLangFrom])
      return
    }
    if (allowSameSourceAndTarget) return
    const filteredLangsTo = langsTo.filter((lang) => lang.languageId !== newLangFrom.languageId)
    if (filteredLangsTo.length !== langsTo.length) {
      setLangsTo(filteredLangsTo)
    }
  }

  return (
    <Stack spacing={stackSpacing} sx={{ p: { xs: 0, md: 4 }, pt: 10 }} direction={isMobile ? "row" : "column"}>
      {!isMobile && <Typography variant="h5">{t("document.languages")}</Typography>}
      <LanguageSelect
        label={isMobile ? t("langFromMobile") : t("langFrom")}
        value={langFrom}
        onChange={(e, val) => val && handleSourceLangChange(val as Language)}
        options={allowedLanguages}
      />
      {!requireSameSourceAndTarget && (
        <LanguageSelect
          multiple={useMultiTargetLangs}
          label={isMobile ? t("langToMobile") : t("langTo")}
          value={useMultiTargetLangs ? langsTo : langsTo[0]}
          onChange={(e, val) => val && (useMultiTargetLangs ? setLangsTo(val as Language[]) : setLangsTo([val as Language]))}
          options={
            allowSameSourceAndTarget
              ? allowedLanguages
              : allowedLanguages.filter((l) => l.languageId !== langFrom.languageId)
          }
        />
      )}
      {requireSameSourceAndTarget && (
        <Tooltip title={t("targetLanguageLockedFinalRedaction")} placement="top">
          <span>
            <LanguageSelect
              label={isMobile ? t("langToMobile") : t("langTo")}
              value={langFrom}
              onChange={() => undefined}
              options={[langFrom]}
              disabled
            />
          </span>
        </Tooltip>
      )}
    </Stack>
  )
}
